import React, { useMemo, useState, useEffect } from "react";
import {
  AppBar, Toolbar, Container, Box, Typography, Card, CardContent, Chip,
  Stack, Divider, TextField, Button, Link, Checkbox, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Paper, Alert,
  CssBaseline, GlobalStyles
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Check, Lock } from "@mui/icons-material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";


// src/api/atlasApi.ts
const API_BASE = "https://pioneer.pspace-research.com:8443/api/v1/invitation_code";

export async function generateCode(domain, sdrSecret) {
  const res = await fetch(`${API_BASE}/codes/generate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-SDR-SECRET": sdrSecret,
    },
    body: JSON.stringify({ domain }),
  });

  if (!res.ok) {
    const msg = await res.text();
    throw new Error(msg || "Code-Generierung fehlgeschlagen");
  }

  return res.json(); // InvitationCode
}

export async function validateCode(code) {
  const res = await fetch(`${API_BASE}/validate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ code }),
  });

  if (!res.ok) {
    return { valid: false };
  }

  return res.json(); // { valid: true }
}

export async function fetchAllCodes(sdrSecret) {
  const res = await fetch(`${API_BASE}/codes/monitor`, {
    headers: {
      "X-SDR-SECRET": sdrSecret,
    },
  });

  if (!res.ok) {
    throw new Error("Unauthorized oder Serverfehler");
  }

  return res.json(); // InvitationCode[]
}


/* -------------------- 1. CONFIGURATION -------------------- */

const BRAND = {
  primary: "#0C056D",
  accent: "#C9A24D",
  logoHeader: "./atlas_pspace_research_logo.png",
  logoFooter: "./pspace_research_logo_filled.png",
};

const CONTENT = {
  hero: {
    title: "Versicherungsvermittler zuverlässig finden",
    subtitle: "für Vertriebsaufbau und Kundenakquise",
    description: "ATLAS ermöglicht als Softwaremodul die Analyse des deutschen Versicherungsvermittlermarkts für Versicherer, Vertriebe und Branchendienstleister.",
    bullets: [
      "Relevante Versicherungsagenturen gezielt identifizieren",
      "Zentrale Übersicht über mehr als 45 Vertriebsorganisationen",
      "Entscheider inklusive Kontaktdaten",
      "Zusatzinformationen aus dem Handelsregister",
      "Segmentierte Bereitstellung statt manueller Recherche",
    ],
    stats: [
      { label: "Recherche", value: "in Sekunden" },
      { label: "Nutzung", value: "CRM-ready" },
      { label: "Exklusiv", value: "für Pioneers" },
    ]
  },
  pioneer: {
    sectionTitle: "Pioneer Program",
    intro: "Das ATLAS Pioneer Program richtet sich an ausgewählte Partner. Pioneers können ATLAS ab sofort produktiv einsetzen und profitieren von dauerhaften Vorteilen.",
    benefits: [
      { title: "Volle Datentiefe", body: "Uneingeschränkter Zugriff auf alle erfassten Informationen" },
      { title: "Dauerhafter Einstiegspreis", body: "Attraktive Preisvorteile über den Produktstart hinaus" },
      { title: "Mitgestaltung", body: "Aktive Mitgestaltung der finalen Produktversion durch Ihr Feedback" },
    ]
  },
  cta: {
    title: "Exklusiven Demo-Termin buchen",
    description: "Der Zugang zum ATLAS Pioneer Program erfolgt im Rahmen eines persönlichen Demo-Gesprächs. Bitte geben Sie Ihren Einladungscode ein.",
    privacyLabel: "Ich habe die Datenschutzerklärung gelesen und akzeptiere die Verarbeitung meiner Daten zur Terminvereinbarung.",
    buttonText: "Demo-Termin buchen",
    errorText: "Dieser Einladungscode ist leider nicht gültig oder bereits abgelaufen.",
    calendlyUrl: "https://cloud.pspace-research.com/index.php/apps/appointments/pub/77qMYNZQc9Vbt08E/form",
  },
  vision: {
    description: "pSpace research entwickelt mit NEXUS eine Plattform zur fortlaufenden Bewertung marktrelevanter Signale. Grundlage sind KI-generierte und durch Menschen verifizierte Bedarfshypothesen, deren Bedeutung aufgrund von realem Marktfeedback automatisch evaluiert wird. ATLAS ist ein produktives Modul innerhalb dieses Systems.",
    title: "Die Nexus-Vision",
    bullets: [
      "ATLAS erkennt relevante Veränderungen, die typischerweise Gesprächsbedarf auslösen.",
      "Konkrete Kontakt- und Bedarfsthemen werden abgeleitet und durch persönlichen Outreach geprüft.",
      "Der Marktwert bestätigter Informationen bestimmt deren tatsächliche Relevanz und steuert künfitge Hypothesen und Empfehlungen.",
    ]
  },
  legal: {
    impressumTitle: "Impressum",
    impressumContent: `pSpace research UG (haftungsbeschränkt) i. G.\nMozartstraße 6\n72661 Grafenberg\n\nVertreten durch: Philipp Kunz\n\nHandelsregister: Amtsgericht Stuttgart\n HRB: folgt\n\nSteuer-ID: folgt\n\nEmail: kontakt@pspace-research.com`,
    privacyPdfUrl: "./datenschutz.pdf"
  },
};


/* -------------------- 3. SDR ADMIN VIEW -------------------- */

function SdrAdminRoute() {
  const [sdrSecret, setSdrSecret] = useState("");
  const [domain, setDomain] = useState("");
  const [generatedCode, setGeneratedCode] = useState("");
  const [error, setError] = useState("");
  const [codes, setCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(false);

  const handleGenerate = async () => {
    try {
      setError("");
      const result = await generateCode(domain, sdrSecret);
      setGeneratedCode(result.code);
    } catch (e) {
      setError(e.message);
    }
  };


  const handleLoadCodes = async () => {
    try {
      setError("");
      setLoadingCodes(true);
      const result = await fetchAllCodes(sdrSecret);
      setCodes(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingCodes(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 10 }}>
      <Paper sx={{ p: 4, borderRadius: 4, border: "1px solid", borderColor: "divider" }}>
        <Stack spacing={3}>
          <Typography variant="h5" fontWeight={700}>SDR Code Generator</Typography>
          <TextField
            label="SDR Secret Key"
            type="password"
            value={sdrSecret}
            onChange={(e) => setSdrSecret(e.target.value)}
            fullWidth
          />
          <TextField
            label="Ziel-Website (z.B. allianz-mueller.de)"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            fullWidth
          />
          <Button variant="contained" onClick={handleGenerate} sx={{ bgcolor: BRAND.primary }}>
            Code generieren
          </Button>
          <Button
            variant="outlined"
            onClick={handleLoadCodes}
            disabled={!sdrSecret}
          >
            Codes anzeigen
          </Button>
          {error && <Alert severity="error">{error}</Alert>}

          {generatedCode && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "rgba(0,0,0,0.03)", borderRadius: 2, border: "1px dashed gray" }}>
              <Typography variant="caption">Generierter Code für {domain}:</Typography>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h4" fontWeight={800} color={BRAND.accent}>{generatedCode}</Typography>
                <IconButton onClick={() => navigator.clipboard.writeText(generatedCode)}>
                  <ContentCopyIcon />
                </IconButton>
              </Stack>
            </Box>
          )}
          {codes.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Alle Einladungscodes
              </Typography>
              <Paper variant="outlined">
                <Box sx={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th align="left">Code</th>
                        <th align="left">Domain</th>
                        <th>Status</th>
                        <th>Erstellt</th>
                        <th>Genutzt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {codes.map((c) => (
                        <tr key={c.id}>
                          <td>{c.code}</td>
                          <td>{c.targetDomain}</td>
                          <td>
                            <Chip
                              size="small"
                              label={c.used ? "Genutzt" : "Aktiv"}
                              color={c.used ? "error" : "success"}
                            />
                          </td>
                          <td>{new Date(c.createdAt).toLocaleString()}</td>
                          <td>
                            {c.usedAt
                              ? new Date(c.usedAt).toLocaleString()
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Paper>
            </Box>
          )}

        </Stack>
      </Paper>
    </Container>
  );
}

/* -------------------- 4. MAIN LANDINGPAGE -------------------- */

export default function App() {
  const [view, setView] = useState("landing"); // Einfaches Routing-System

  return (
    <>
      {/* ✅ Globale adaptive Skalierung über rem-Basis, ohne Schriftart zu ändern */}
      <CssBaseline />
      <GlobalStyles
        styles={{
          html: {
            fontSize: "clamp(14px, 1.1vw, 18px)",
          },
        }}
      />

      {view === "sdr" ? (
        <SdrAdminView onBack={() => setView("landing")} />
      ) : (
        <AtlasPioneerLanding onOpenAdmin={() => setView("sdr")} />
      )}
    </>
  );
}

// Separate Komponente für die Landingpage
function AtlasPioneerLanding({ onOpenAdmin }) {
  const [code, setCode] = useState("");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [touched, setTouched] = useState(false);
  const [impressumOpen, setImpressumOpen] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isCodeValid, setIsCodeValid] = useState(false);

  // Dynamische Validierung gegen das "Backend"

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (code.length >= 6) {
        setIsValidating(true);
        try {
          const result = await validateCode(code);
          setIsCodeValid(result.valid);
          if (!result.valid) setPrivacyAccepted(false);
        } catch {
          setIsCodeValid(false);
          setPrivacyAccepted(false);
        } finally {
          setIsValidating(false);
        }
      } else {
        setIsCodeValid(false);
        setPrivacyAccepted(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [code]);

  const canSubmit = isCodeValid && privacyAccepted;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', overflowX: "hidden" }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: BRAND.primary }}>
        <Toolbar>
          <img src={BRAND.logoHeader} alt="Logo" height={40} style={{ margin: "25px" }} />
          <Box sx={{ flexGrow: 1 }} />
          <Chip label="Pioneer Program" sx={{ color: "white", bgcolor: "rgba(255,255,255,0.15)" }} />
        </Toolbar>
      </AppBar>

      <Box component="main">
        {/* HERO SECTION */}
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Stack spacing={4}>
            <Stack spacing={1}>
              <Typography
                variant="h3"
                fontWeight={600}
                component="h1"
                sx={{
                  // ✅ beides: deine responsive Größen + kein Overflow
                  fontSize: {
                    xs: '2rem',
                    sm: '2.5rem',
                    md: '3rem',
                    lg: '3.5rem'
                  },
                  lineHeight: 1.2,
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  hyphens: "auto",
                  maxWidth: "100%",
                }}
              >
                {CONTENT.hero.title}
              </Typography>

              <Typography
                variant="h6"
                fontWeight={500}
                sx={{
                  color: BRAND.primary,
                  fontSize: {
                    xs: '1rem',
                    sm: '1.25rem'
                  }
                }}
              >
                {CONTENT.hero.subtitle}
              </Typography>
            </Stack>

            <Typography color="text.secondary" sx={{ maxWidth: 900, fontSize: '1.1rem' }}>
              {CONTENT.hero.description}
            </Typography>

            <Stack spacing={2}>
              {CONTENT.hero.bullets.map((t) => (
                <Stack key={t} direction="row" spacing={2} alignItems="flex-start">
                  <Check fontSize="small" sx={{ mt: "3px", color: BRAND.primary }} />
                  <Typography variant="body1">{t}</Typography>
                </Stack>
              ))}
            </Stack>

            {/* ✅ FIX: Stats/Highlights laufen nicht mehr über */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3}
              useFlexGap
              flexWrap="wrap"
              sx={{ alignItems: "stretch" }}
            >
              {CONTENT.hero.stats.map((stat) => (
                <Box
                  key={stat.label}
                  sx={{
                    flex: { xs: "1 1 100%", sm: "1 1 180px" },
                    minWidth: { xs: "100%", sm: 180 },
                    maxWidth: { xs: "100%", sm: 240 },
                  }}
                >
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: 4,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: BRAND.accent,
                      color: "white",
                    }}
                  >
                    <CardContent sx={{ textAlign: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.9,
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          hyphens: "auto",
                        }}
                      >
                        {stat.label}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          hyphens: "auto",
                        }}
                      >
                        {stat.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Stack>

          </Stack>
        </Container>

        {/* IMAGE DIVIDER */}
        <Box sx={{
          height: { xs: 320, md: 420 }, my: 10,
          backgroundImage: `linear-gradient(rgba(12,5,109,0.55), rgba(12,5,109,0.55)), url('./koehlbrandbrücke.png')`,
          backgroundSize: "cover", backgroundPosition: "center", display: "flex", alignItems: "center",
        }}>
          <Container maxWidth="lg">
            <Box sx={{ display: "inline-block", px: 3.5, py: 1.5, borderRadius: 999, border: "2px solid rgba(255,255,255,0.85)", backdropFilter: "blur(6px)" }}>
              <Typography variant="h4" fontWeight={800} color="white">{CONTENT.pioneer.sectionTitle}</Typography>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Stack spacing={6} maxWidth={900} sx={{ mx: "auto" }}>

            <Typography color="text.secondary" sx={{ fontSize: '1.0rem', lineHeight: 1.6 }}>
              {CONTENT.pioneer.intro}
            </Typography>

            <Grid
              container
              spacing={4}
              sx={{
                width: "100% !important",
                margin: "0 !important"
              }}
            >
              {CONTENT.pioneer.benefits.map((benefit, index) => (
                <Grid item xs={12} sm={6} md={4} key={benefit.title}>
                  <Stack spacing={2} sx={{ height: '100%', position: 'relative' }}>
                    {/* Akzent-Linie oben für optische Struktur */}
                    <Box sx={{ width: 40, height: 3, bgcolor: BRAND.accent, borderRadius: 1 }} />

                    <Stack spacing={1}>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{
                          color: BRAND.primary,
                          lineHeight: 1.2,
                          hyphens: "auto",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                        }}
                      >
                        {benefit.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.6 }}
                      >
                        {benefit.body}
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Container>

        {/* CTA SECTION - HIER IST DIE LOGIK */}
        <Box sx={{ py: 10, bgcolor: "rgba(12,5,109,0.04)", borderY: "1px solid", borderColor: "divider", mt: 9 }}>
          <Container maxWidth="md">
            <Stack spacing={3}>
              <Typography variant="h5" fontWeight={700}>{CONTENT.cta.title}</Typography>
              <Typography color="text.secondary">{CONTENT.cta.description}</Typography>

              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    value={code}
                    onChange={(e) => { setCode(e.target.value); setTouched(true); }}
                    label="Einladungscode"
                    placeholder="ATLAS-XXXXX"
                    fullWidth
                    error={touched && !isCodeValid && code.length > 5}
                    helperText={isCodeValid ? "Code verifiziert ✓" : ""}
                  />
                  <Button
                    size="large" variant="contained"
                    href={canSubmit ? CONTENT.cta.calendlyUrl : undefined}
                    target="_blank"
                    disabled={!canSubmit}
                    sx={{ minWidth: 240, bgcolor: BRAND.primary }}
                  >
                    {CONTENT.cta.buttonText}
                  </Button>
                </Stack>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      disabled={!isCodeValid}
                      sx={{ color: BRAND.primary, '&.Mui-checked': { color: BRAND.primary } }}
                    />
                  }
                  label={
                    <Typography variant="body2" color={isCodeValid ? "text.primary" : "text.disabled"}>
                      {CONTENT.cta.privacyLabel}
                    </Typography>
                  }
                />
                {touched && !isCodeValid && code.length > 5 && !isValidating && (
                  <Typography variant="body2" color="error">{CONTENT.cta.errorText}</Typography>
                )}
              </Stack>
            </Stack>
          </Container>
        </Box>

        {/* VISION */}
        <Container maxWidth="lg" sx={{ py: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}><img src={BRAND.logoFooter} width={200} alt="Logo" /></Box>
          <Typography color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>{CONTENT.vision.description}</Typography>
          <Box sx={{ p: 4, borderRadius: 3, bgcolor: "rgba(0,0,0,0.02)", border: "1px solid", borderColor: "divider" }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 3 }}>{CONTENT.vision.title}</Typography>
            <Stack spacing={2}>
              {CONTENT.vision.bullets.map((t) => (
                <Stack key={t} direction="row" spacing={2} alignItems="flex-start">
                  <Check fontSize="small" sx={{ mt: "3px", color: BRAND.primary }} />
                  <Typography variant="body1">{t}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>
               
      <Button color="inherit" onClick={onOpenAdmin} size="small" sx={{ opacity: 0.1, mr: 2 }}>SDR</Button>

      {/* FOOTER */}
      <Box component="footer" sx={{ py: 6, mt: 'auto', bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack direction="row" spacing={3}>
              <Link component="button" variant="body2" onClick={() => setImpressumOpen(true)} sx={{ color: 'text.secondary', cursor: 'pointer', textDecoration: 'none' }}>Impressum</Link>
              <Link href={CONTENT.legal.privacyPdfUrl} target="_blank" variant="body2" sx={{ color: 'text.secondary', textDecoration: 'none' }}>Datenschutz</Link>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* IMPRESSUM */}
      <Dialog open={impressumOpen} onClose={() => setImpressumOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {CONTENT.legal.impressumTitle}
          <IconButton onClick={() => setImpressumOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers><Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{CONTENT.legal.impressumContent}</Typography></DialogContent>
        <DialogActions><Button onClick={() => setImpressumOpen(false)}>Schließen</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

function SdrAdminView({ onBack }) {
  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      <AppBar position="static" sx={{ bgcolor: "black" }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>pSpace Admin</Typography>
          <Button color="inherit" onClick={onBack}>Zurück zur Landingpage</Button>
        </Toolbar>
      </AppBar>
      <SdrAdminRoute />
    </Box>
  );
}
